import { __decorate, __metadata, __param } from "tslib";
import { Symbols } from '@/symbols';
import { injectable, inject } from 'inversify';
import { E_OpenMode, E_ResourceSpaceContentRuleScope, E_ResourceSpaceContentRuleType, E_ResourceSpaceContentType, E_ResourceSpaceContentStatus } from '@/apis/service/operation-center/OperationCenterApi.type';
import _ from 'lodash';
import { E_ENABLED_STATUS } from '../CommonOptionsService.type';
let ProductResourceSpaceService = class ProductResourceSpaceService {
    constructor(operationCenterApi, i18n, productLineService) {
        this.operationCenterApi = operationCenterApi;
        this.i18n = i18n;
        this.productLineService = productLineService;
    }
    get productLine() {
        return this.productLineService.productLine;
    }
    /**
     * 只能输入正整数，1-9999
     */
    orderFormatter(value) {
        value && (value = parseInt(value));
        if (value < 1) {
            return null;
        }
        if (value > 9999) {
            value = parseInt(value.toString().slice(0, -1));
        }
        return value;
    }
    getCurrentProductLine(product_id) {
        return this.productLineService.getCurrentProductLine(product_id);
    }
    getProductLine() {
        const product_line_type = this.productLineService.productLineType.RESOURCE_SPACE_CONTENT;
        return this.productLineService.getProductLine(product_line_type);
    }
    postProductLine(product_line_key) {
        const params = {
            product_line_key: product_line_key,
            product_line_type: this.productLineService.productLineType.RESOURCE_SPACE_CONTENT,
        };
        return this.productLineService.postProductLine(params);
    }
    deleteProductLine(id) {
        return this.productLineService.deleteProductLine(id);
    }
    // #endregion
    // #region 资源位
    getResourceSpaceList(product_line_id) {
        return this.operationCenterApi.getResourceSpaceList(product_line_id);
    }
    getCurrentResourceSpace(list, id) {
        return _.find(list, i => i.id === id);
    }
    postResourceSpace(params) {
        return this.operationCenterApi.postResourceSpace(params);
    }
    getResourceSpace(resource_space_id) {
        return this.operationCenterApi.getResourceSpace(resource_space_id);
    }
    putResourceSpace(params) {
        return this.operationCenterApi.putResourceSpace(params);
    }
    deleteResourceSpace(resource_space_id) {
        return this.operationCenterApi.deleteResourceSpace(resource_space_id);
    }
    canDeleteResourceSpace(item) {
        return item.status !== E_ENABLED_STATUS.ACTIVE;
    }
    // #endregion
    // #region 资源位内容
    get contentTypeOptions() {
        return [
            { label: this.i18n.t('backoffice.productResourceSpace.resourceSpaceContentType.Link'), value: E_ResourceSpaceContentType.Link },
            { label: this.i18n.t('backoffice.productResourceSpace.resourceSpaceContentType.Video'), value: E_ResourceSpaceContentType.Video },
            { label: this.i18n.t('backoffice.productResourceSpace.resourceSpaceContentType.Playbook'), value: E_ResourceSpaceContentType.Playbook }
        ];
    }
    get contentOpenModeOptions() {
        return [
            { label: this.i18n.t('backoffice.productResourceSpace.openMode.newTab'), value: E_OpenMode.NewTab },
            { label: this.i18n.t('backoffice.productResourceSpace.openMode.popup'), value: E_OpenMode.Popup }
        ];
    }
    get contentRuleScopeOptions() {
        return [
            {
                label: this.i18n.t('backoffice.productResourceSpace.contentRuleScope.all'),
                value: E_ResourceSpaceContentRuleScope.AllUsers,
                desc: '',
            },
            {
                label: this.i18n.t('backoffice.productResourceSpace.contentRuleScope.assign'),
                value: E_ResourceSpaceContentRuleScope.SelectedUsers,
                desc: this.i18n.t('backoffice.productResourceSpace.contentRuleScope.assignTip'),
            }
        ];
    }
    getRuleScope(rule_scope) {
        return _.find(this.contentRuleScopeOptions, item => item.value === rule_scope);
    }
    get contentRuleTypeOptions() {
        return [
            {
                label: this.i18n.t('backoffice.productResourceSpace.contentRuleType.include'),
                value: E_ResourceSpaceContentRuleType.Include,
                desc: this.i18n.t('backoffice.productResourceSpace.contentRuleType.includeTip'),
            },
            {
                label: this.i18n.t('backoffice.productResourceSpace.contentRuleType.exclude'),
                value: E_ResourceSpaceContentRuleType.Exclude,
                desc: this.i18n.t('backoffice.productResourceSpace.contentRuleType.excludeTip'),
            }
        ];
    }
    getRuleType(rule_type) {
        return _.find(this.contentRuleTypeOptions, item => item.value === rule_type);
    }
    getResourceSpaceContentPagination(params, product_id) {
        return this.operationCenterApi.getResourceSpaceContentPagination({
            ...params,
            product_line_id: this.getCurrentProductLine(product_id).id,
        });
    }
    postResourceSpaceContent(params) {
        return this.operationCenterApi.postResourceSpaceContent(params);
    }
    deleteResourceSpaceContent(resource_space_content_id) {
        return this.operationCenterApi.deleteResourceSpaceContent(resource_space_content_id);
    }
    getResourceSpaceContentDetail(content_id) {
        return this.operationCenterApi.getResourceSpaceContentDetail(content_id);
    }
    putResourceSpaceContent(params) {
        return this.operationCenterApi.putResourceSpaceContent(params);
    }
    getContentStatus(status) {
        if (status === E_ResourceSpaceContentStatus.Active) {
            return this.i18n.t('backoffice.productResourceSpace.contentStatus.active');
        }
        if (status === E_ResourceSpaceContentStatus.Inactive) {
            return this.i18n.t('backoffice.productResourceSpace.contentStatus.inactive');
        }
        return this.i18n.t('backoffice.productResourceSpace.contentStatus.draft');
    }
    getContentStatusBadgeVariant(status) {
        if (status === E_ResourceSpaceContentStatus.Active) {
            return 'success';
        }
        if (status === E_ResourceSpaceContentStatus.Inactive) {
            return 'warning';
        }
        return 'secondary';
    }
    canEditContent(item) {
        return item.status !== E_ResourceSpaceContentStatus.Active;
    }
    canCopyContent(item) {
        return item.status === E_ResourceSpaceContentStatus.Active;
    }
    canDeleteContent(item) {
        return item.status !== E_ResourceSpaceContentStatus.Active;
    }
    canOnlineContent(item) {
        return item.status !== E_ResourceSpaceContentStatus.Active;
    }
    canOfflineContent(item) {
        return item.status === E_ResourceSpaceContentStatus.Active;
    }
    postResourceSpaceContentClone(resource_space_content_id) {
        return this.operationCenterApi.postResourceSpaceContentClone(resource_space_content_id);
    }
    getResourceSpaceContentOnShelvesCheck(resource_space_content_id) {
        return this.operationCenterApi.getResourceSpaceContentOnShelvesCheck(resource_space_content_id);
    }
    postResourceSpaceContentOnShelves(resource_space_content_id) {
        return this.operationCenterApi.postResourceSpaceContentOnShelves(resource_space_content_id);
    }
    getResourceSpaceContentOffShelvesCheck(resource_space_content_id) {
        return this.operationCenterApi.getResourceSpaceContentOffShelvesCheck(resource_space_content_id);
    }
    postResourceSpaceContentOffShelves(resource_space_content_id) {
        return this.operationCenterApi.postResourceSpaceContentOffShelves(resource_space_content_id);
    }
    // #endregion
    getFileName(file) {
        if (file instanceof File) {
            return file.name;
        }
        // 截掉? 后面的参数, 取最后一个/后面的内容
        if (file) {
            return decodeURIComponent(file).split('?')[0].split('/').pop();
        }
    }
    deleteConfirm(vm) {
        return vm.$swal({
            title: this.i18n.t('backoffice.alert.deleteTitle'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: this.i18n.t('backoffice.common.yes'),
            cancelButtonText: this.i18n.t('backoffice.common.no'),
            customClass: {
                confirmButton: 'btn btn-primary',
                cancelButton: 'btn btn-outline-danger ml-1',
            },
            buttonsStyling: false,
        }).then((result) => result.value);
    }
};
ProductResourceSpaceService = __decorate([
    injectable(),
    __param(0, inject(Symbols.OperationCenterApi)),
    __param(1, inject(Symbols.I18n)),
    __param(2, inject(Symbols.ProductLineService)),
    __metadata("design:paramtypes", [Object, Object, Object])
], ProductResourceSpaceService);
export { ProductResourceSpaceService };
